import { useState, useCallback, useEffect } from 'react';
import { ServiceRequest, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { OrderFormData, OrderFormErrors } from '../components/OrderForm';
import {
  createServiceRequestFromForm,
  uploadOrderDocumentsAndGetRefs,
} from '../utils/fhir-helpers';
import { validateOrderForm } from '../utils/order-validation';
import { ARKON } from '@consts/systems.consts';

export function useOrderForm(initialData?: Partial<OrderFormData>) {
  const [formData, setFormData] = useState<OrderFormData>({
    patient: initialData?.patient || undefined,
    requester: initialData?.requester || undefined,
    testCode: initialData?.testCode || '',
    testDisplay: initialData?.testDisplay || '',
    sampleType: initialData?.sampleType || undefined,
    clinicalNotes: initialData?.clinicalNotes || '',
    answerDelivery: initialData?.answerDelivery ?? [],
    authorizedPerson: initialData?.authorizedPerson || '',
    authorizationFormFile: initialData?.authorizationFormFile || undefined,
    orderDate: initialData?.orderDate ?? new Date(),
    orderFile: initialData?.orderFile || undefined,
    referralReason: initialData?.referralReason || '',
    symptomOnsetAge: initialData?.symptomOnsetAge || '',
    firstSymptom: initialData?.firstSymptom || '',
  });

  const [errors, setErrors] = useState<OrderFormErrors>({});

  const updateField = useCallback(
    <K extends keyof OrderFormData>(field: K, value: OrderFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field when it's updated
      setErrors((prev) => {
        if (prev[field as keyof OrderFormErrors]) {
          const newErrors = { ...prev };
          delete newErrors[field as keyof OrderFormErrors];
          return newErrors;
        }
        return prev;
      });
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData({
      patient: undefined,
      requester: undefined,
      testCode: '',
      testDisplay: '',
      sampleType: undefined,
      clinicalNotes: '',
      answerDelivery: [],
      authorizedPerson: '',
      authorizationFormFile: undefined,
      orderDate: new Date(),
      orderFile: undefined,
      referralReason: '',
      symptomOnsetAge: '',
      firstSymptom: '',
    });
    setErrors({});
  }, []);

  const setFormErrors = useCallback((newErrors: OrderFormErrors) => {
    setErrors(newErrors);
  }, []);

  return {
    formData,
    errors,
    updateField,
    resetForm,
    setFormErrors,
    setFormData,
  };
}

interface UseOrderFormWithSaveOptions {
  serviceRequest: ServiceRequest | null;
  onSuccess?: () => Promise<void> | void;
  documentsToDelete?: string[];
}

export function useOrderFormWithSave(
  options: UseOrderFormWithSaveOptions,
  initialData?: Partial<OrderFormData>
) {
  const base = useOrderForm(initialData);
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async (): Promise<boolean> => {
    const validation = validateOrderForm(base.formData);
    if (!validation.isValid || !options.serviceRequest) {
      base.setFormErrors(validation.errors);
      return false;
    }

    setSaving(true);

    try {
      const orderId = options.serviceRequest.identifier?.[0]?.value || '';
      const docsToDelete = options.documentsToDelete ?? [];

      // Filter out documents marked for deletion from supportingInfo
      let supportingInfo = options.serviceRequest.supportingInfo ?? [];
      if (docsToDelete.length > 0) {
        supportingInfo = supportingInfo.filter((ref) => {
          const docId = ref.reference?.replace('DocumentReference/', '');
          return !docId || !docsToDelete.includes(docId);
        });
        // Soft delete: Update DocumentReference status to 'entered-in-error'
        for (const docId of docsToDelete) {
          try {
            const docRef = await medplum.readResource(
              'DocumentReference',
              docId
            );
            await medplum.updateResource({
              ...docRef,
              status: 'entered-in-error',
            });
          } catch (err) {
            console.warn(
              `Failed to mark DocumentReference ${docId} as entered-in-error:`,
              err
            );
          }
        }
      }

      // Upload any new order file / authorization form and add their refs
      const newDocRefs = await uploadOrderDocumentsAndGetRefs(
        medplum,
        base.formData,
        orderId,
        supportingInfo
      );
      if (newDocRefs.length > 0) {
        supportingInfo = [...supportingInfo, ...newDocRefs];
      }

      const newServiceRequest = createServiceRequestFromForm(
        base.formData,
        orderId,
        options.serviceRequest.status
      );

      // Merge meta: keep server-managed fields (versionId, lastUpdated) from the
      // existing resource but use the freshly computed tags from the form so
      // changes like specimen type are persisted.
      const existingMeta = options.serviceRequest.meta ?? {};
      const newTags = newServiceRequest.meta?.tag ?? [];
      // Preserve tags that aren't managed by createServiceRequestFromForm
      const preservedTags = (existingMeta.tag ?? []).filter(
        (t) =>
          t.system !== ARKON.SD.LAB_ORDER_SUBSTATE &&
          t.system !== ARKON.SD.SPECIMEN_TYPE
      );

      const updatedServiceRequest = {
        ...newServiceRequest,
        id: options.serviceRequest.id,
        meta: {
          ...existingMeta,
          tag: [...preservedTags, ...newTags],
        },
        supportingInfo,
      };

      await medplum.updateResource(updatedServiceRequest);

      notifications.show({
        title: t('success', { ns: 'common' }),
        message: t('orderDetails.notifications.updateSuccess', { ns: 'lims' }),
        color: 'green',
      });

      await options.onSuccess?.();

      return true;
    } catch (error: any) {
      notifications.show({
        title: t('orderDetails.notifications.updateError', { ns: 'lims' }),
        message:
          error?.message ||
          t('orderDetails.notifications.unexpectedError', { ns: 'lims' }),
        color: 'red',
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [base, medplum, options, t]);

  return {
    ...base,
    saving,
    handleSave,
  };
}

export function usePrefillOrderForm(
  patientId: string | undefined,
  referrerId: string | undefined,
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>
) {
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    const prefillForm = async () => {
      if (!patientId && !referrerId) return;

      setInitializing(true);
      try {
        const updates: Partial<OrderFormData> = {};

        if (patientId) {
          const patient = await medplum.readResource('Patient', patientId);
          updates.patient = patient as Patient;
        }

        if (referrerId) {
          const requester = await medplum.readResource(
            'Practitioner',
            referrerId
          );
          updates.requester = requester as Practitioner;
        }

        setFormData((prev) => ({ ...prev, ...updates }));
      } catch (error) {
        console.error('Error prefilling form:', error);
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: 'Could not load patient or requester information',
          color: 'red',
        });
      } finally {
        setInitializing(false);
      }
    };

    prefillForm();
  }, [patientId, referrerId, medplum, setFormData, t]);

  return { initializing };
}
